import React, { useState, useContext, useEffect } from 'react';

import { PageHeader, Row, Col } from 'antd';

import styled from 'styled-components';

import { saveAlerts } from '@libs/NwAlerts';

import { useStateValue } from '@contexts/StateProvider';
import { UserContext, useUserPermission } from '@contexts/UserContext';

import { useContact } from '@hooks/API/Contact/UseContact';
import { modelFullName } from '@hooks/API/Contact/UseContactsList';
import { useAddContactTag, useRemoveContactTag } from '@hooks/API/Contact/UseContactTags';
import useLocalStorageState from '@hooks/Utils/UseLocalStorageState';
import usePlaceholderImageFixed from '@hooks/Utils/UsePlaceholderImageFixed';

import AnagraphicalDrawer from '@components/ContactDetails/CommonComponents/Anagraphical/AnagraphicalDrawer';
import ModelDetailsCover from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsHeader/ModelDetailsCover';
import ModelDetailsHeaderAgencies from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsHeader/ModelDetailsHeaderAgencies';
import ModelDetailsHeaderFooter from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsHeader/ModelDetailsHeaderFooter';

import AlertDrawer from '@components/AlertDrawer/AlertDrawer';
import ContactAlert from '@components/ContactAlert/ContactAlert';
import ContactAlertDrawer from '@components/ContactAlert/ContactAlertDrawer';
import ContactDepartments from '@components/ContactDepartments/ContactDepartments';
import ContactDetailsBankAccounts from '@components/ContactDetails/ContactDetailsHeader/ContactDetailsBankAccounts';
import ContactDetailsHeaderAnagraphical from '@components/ContactDetails/ContactDetailsHeader/ContactDetailsHeaderAnagraphical';
import ContactDetailsHeaderConnections from '@components/ContactDetails/ContactDetailsHeader/ContactDetailsHeaderConnections';
import ContactDetailsHeaderLegal from '@components/ContactDetails/ContactDetailsHeader/ContactDetailsHeaderLegal';
import ContactHeaderBillingInfos from '@components/ContactDetails/ContactDetailsHeader/ContactHeaderBillingInfos';
import AddToFolderButton from '@components/ContactsFolders/AddToFolderButton';
import ContactUsers from '@components/ContactUsers/ContactUsers';
import FlexContainer from '@components/Gui/FlexContainer';
import { Loading } from '@components/Gui/Loading';
import NwDividerVertical from '@components/Gui/NwDividerVertical';
import NwDrawer from '@components/Gui/NwDrawer';
import NwIcon from '@components/Gui/NwIcon';
import ModelInTown from '@components/ModelInTown/ModelInTown';
import ObjectLogHistories from '@components/ObjectLogHistories/ObjectLogHistories';
import PermissionBlock from '@components/PermissionBlock/PermissionBlock';
import ObjectTagPicker from '@components/TagPicker/ObjectTagPicker';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const ModelPageHeader = styled(PageHeader)`
    height: ${(props) => props.$collapseView && '8rem !important'};

    .ant-page-header-heading {
        padding-left: ${(props) => props.$collapseView && '6.5rem !important'};
        padding-top: ${(props) => props.$collapseView && '0.5rem !important'};
    }

    .header-content {
        padding-left: ${(props) => props.$collapseView && '6.5rem !important'};
        .header-body {
            padding-left: ${(props) => props.$collapseView && '0 !important'};
        }
    }
    .ant-page-header-heading-title {
        width: 100%;
    }
    .ant-page-header-heading-left {
        width: 100%;
    }
`;

const NameWrapper = styled.div`
    font-size: ${(props) => (props.$collapseView ? '1.4rem' : '2rem')};
    line-height: 1.25;
    text-transform: capitalize;
    padding-right: 1em;
    cursor: pointer;

    h1 {
        font-size: ${(props) => (props.$collapseView ? '1.4rem' : '2rem')};
        margin: 0;
        font-weight: 600;
    }

    /* &.name-with-alert {
        margin-left: 24px;
    } */
`;

const ModelDetailsHeaderDrawer = ({ onOpenModelAgencies, onOpenStatements, selectedModel }) => {
    const { data: currentContact } = useContact(selectedModel);
    const [{ areaContext }] = useStateValue();
    const { mutate: addTag } = useAddContactTag();
    const { mutate: removeTag } = useRemoveContactTag();
    const { currentUser } = useContext(UserContext);
    const [IsLoading, setIsLoading] = useState(true);
    const [showAlertDrawer, setShowAlertDrawer] = useState(false);
    const [readAlerts, setReadAlerts] = useLocalStorageState(`${areaContext.area}-read-alerts`, []);
    const [isOpenHistory, setIsOpenHistory] = useState(false);
    const alertOnModels = useUserPermission(
        'AlertOnModels',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const tagsOnContacts = useUserPermission(
        'TagsOnContacts',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const [anagraphicalDrawer, setAnagraphicalDrawer] = useState(false);

    const handleOpenAnagraphicalDrawer = () => {
        setAnagraphicalDrawer(true);
    };

    useEffect(() => {
        if (currentContact) {
            setIsLoading(false);
        }
    }, [currentContact]);

    // const handleOpenHistory = () => {
    //     setIsOpenHistory(true);
    // };

    const handleCloseHistory = () => {
        setIsOpenHistory(false);
    };

    const onAddTag = (tag) => {
        addTag({ tag: tag, contactID: currentContact.ID });
    };

    const onRemoveTag = (tag) => {
        removeTag({ tag: tag, contactID: currentContact.ID });
    };

    const handleOpenAlertDrawer = () => {
        setShowAlertDrawer(true);
    };

    const handleCloseAlertDrawer = (updated) => {
        if (updated) {
            const alerts = readAlerts.filter(
                (alert) => alert.Author !== currentUser.UserID || alert.ContactID !== currentContact.ID
            );
            setReadAlerts(alerts);
            saveAlerts(alerts);
        }
        setShowAlertDrawer(false);
    };

    const coverguid =
        currentContact && currentContact.NetworkAgencies[0] && currentContact.NetworkAgencies[0].CoverGuid;
    const subType = currentContact && currentContact.SubType;

    const [urlbg, isPlaceholder] = usePlaceholderImageFixed(coverguid, subType);

    return currentContact ? (
        IsLoading ? (
            <Loading />
        ) : (
            <>
                <ContactAlertDrawer contactId={currentContact.ID} />
                <div className="details-header-container details-header-container-drawer">
                    <ModelPageHeader
                        onBack={null}
                        title={
                            <FlexContainer wrap="wrap" justifyContent="space-between" fullWidth>
                                <FlexContainer wrap="wrap">
                                    <NameWrapper>
                                        <h1 onClick={handleOpenAnagraphicalDrawer}>
                                            {modelFullName(currentContact).toLowerCase()}
                                        </h1>
                                        {anagraphicalDrawer && (
                                            <AnagraphicalDrawer
                                                currentContact={currentContact}
                                                type={currentContact.ContactType === 'Customer' ? 0 : 1}
                                                onClose={() => {
                                                    setAnagraphicalDrawer(false);
                                                }}
                                            />
                                        )}
                                    </NameWrapper>
                                    <span className="name-icon">
                                        <AddToFolderButton ids={[currentContact.ID]} type="model" size="lg" showBadge />
                                    </span>
                                    <span className="name-icon">
                                        {alertOnModels ? (
                                            <NwIcon
                                                icon={byPrefixAndName.fasl['comment-alt-exclamation']}
                                                plus={!currentContact.Alert}
                                                edit={currentContact.Alert}
                                                pointer
                                                onClick={handleOpenAlertDrawer}
                                            />
                                        ) : (
                                            <ContactAlert
                                                currentContact={currentContact}
                                                readOnly
                                                icon={
                                                    <NwIcon
                                                        size="lg"
                                                        icon={byPrefixAndName.fasl['comment-alt-exclamation']}
                                                        pointer
                                                    />
                                                }
                                            />
                                        )}
                                    </span>
                                    <span className="name-icon">
                                        <PermissionBlock
                                            permission={tagsOnContacts}
                                            content={
                                                <ObjectTagPicker
                                                    readOnly={!tagsOnContacts}
                                                    itemType={currentContact.ContactType}
                                                    tags={currentContact.Tags}
                                                    onAddTag={tagsOnContacts && onAddTag}
                                                    onRemoveTag={tagsOnContacts && onRemoveTag}
                                                    stripMaxWidth="14rem"
                                                />
                                            }
                                        />
                                    </span>
                                </FlexContainer>
                            </FlexContainer>
                        }
                        subTitle={false}
                    >
                        <ModelDetailsCover
                            urlBg={urlbg}
                            isPlaceholder={isPlaceholder}
                            currentContact={currentContact}
                            readOnly
                        />
                        <div className="header-content">
                            <div className="header-body">
                                <Row>
                                    <Col span={24}>
                                        <div className="header-body-boxes-container">
                                            <ModelInTown
                                                modelID={currentContact.ID}
                                                modelInTownSchedule={currentContact.InTownSchedule}
                                            />
                                            <NwDividerVertical />
                                            <ModelDetailsHeaderAgencies
                                                model={currentContact}
                                                onOpenModelAgencies={onOpenModelAgencies}
                                            />
                                            <NwDividerVertical />
                                            <div className="header-body-boxes header-body-boxes-hoverable">
                                                <ContactUsers
                                                    users={currentContact.Users}
                                                    contactID={currentContact.ID}
                                                />
                                            </div>
                                            <NwDividerVertical />
                                            <div className="header-body-boxes">
                                                <ContactDepartments
                                                    departments={currentContact.Departments}
                                                    contactID={currentContact.ID}
                                                />
                                            </div>
                                        </div>
                                        <div className="header-body-boxes-container model-info-line">
                                            <ContactDetailsHeaderAnagraphical contact={currentContact} />
                                            <div className="header-models-counters">
                                                <NwDividerVertical />
                                                <ContactDetailsHeaderLegal currentContact={currentContact} />
                                                <NwDividerVertical />
                                                <ContactHeaderBillingInfos
                                                    type="model"
                                                    currentContact={currentContact}
                                                />
                                                <NwDividerVertical />
                                                <ContactDetailsBankAccounts currentContact={currentContact} />
                                            </div>
                                        </div>
                                    </Col>
                                    <Col span={24}>
                                        <ContactDetailsHeaderConnections contact={currentContact} inDrawer />
                                    </Col>
                                </Row>
                            </div>
                        </div>
                        <ModelDetailsHeaderFooter
                            currentContact={currentContact}
                            onOpenModelAgencies={onOpenModelAgencies}
                            onOpenStatements={onOpenStatements}
                            disableStatement={areaContext.area === 'scouting' || !currentContact.Approved}
                            hideActions
                        />
                    </ModelPageHeader>
                </div>
                {showAlertDrawer && <AlertDrawer currentContact={currentContact} onClose={handleCloseAlertDrawer} />}
                {isOpenHistory && (
                    <NwDrawer title="Model Logs" onClose={handleCloseHistory}>
                        <ObjectLogHistories objectId={currentContact.ID} type="model" />
                    </NwDrawer>
                )}
            </>
        )
    ) : (
        <Loading />
    );
};

export default ModelDetailsHeaderDrawer;
